import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import FormLoadingOverlay from '../components/common/FormLoadingOverlay';

interface ScrollContextType {
  saveEditFormPosition: (position: number) => void;
  getEditFormPosition: () => number;
}

const ScrollContext = createContext<ScrollContextType | null>(null);

export const ScrollProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Single ref for edit form scroll position
  const editFormScrollPosition = useRef<number>(0);

  const saveEditFormPosition = (position: number) => {
    editFormScrollPosition.current = position;
  };

  const getEditFormPosition = (): number => {
    return editFormScrollPosition.current;
  };

  return (
    <ScrollContext.Provider
      value={{ saveEditFormPosition, getEditFormPosition }}
    >
      {children}
    </ScrollContext.Provider>
  );
};

export const useScroll = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScroll must be used within ScrollProvider');
  }
  return context;
};

interface ScrollRestorationProps {
  children: React.ReactNode;
  isRestoring: boolean;
  scrollOffset: number;
  loadingText?: string;
}

const ScrollRestoration: React.FC<ScrollRestorationProps> = ({
  children,
  isRestoring,
  scrollOffset = 20,
  loadingText = 'Loading listing...',
}) => {
  const [isLoading, setIsLoading] = useState(isRestoring);
  const { getEditFormPosition } = useScroll();

  useEffect(() => {
    if (isRestoring) {
      setIsLoading(true);
      const savedPosition = getEditFormPosition();

      setTimeout(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: savedPosition - scrollOffset,
            behavior: 'auto',
          });
          // Allow progress bar to reach completion
          setIsLoading(false);
        });
      }, 1); // Slightly longer initial delay to allow for progress animation
    }
  }, [isRestoring, getEditFormPosition]);

  return (
    <>
      {isLoading && <FormLoadingOverlay text={loadingText} />}
      {children}
    </>
  );
};

export { ScrollRestoration };
